import { useState, useCallback, useEffect } from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { useQueryClient } from "@tanstack/react-query"
import {
  X, UserPlus, MessageSquare, Mail, RotateCw, MessageCircle,
  CheckCircle, XCircle, Linkedin, Copy, ExternalLink, Check,
  Loader2, AlertTriangle, Lock, Link2, ChevronRight,
  ArrowUpRight, ArrowDownLeft, Sparkles,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  useCreateEvent, useUpdateOutreachStatus, useGenerateMessages,
  useMarkConnected, useNextStepSuggestion, useContactTimeline,
} from "@/hooks/useOutreach"
import { useOpportunities } from "@/hooks/useOpportunities"
import { OUTREACH_STATUS_CONFIG } from "@/types/outreach"
import type { QueueItem, OutreachStatus, OutreachEventType } from "@/types/outreach"

// ── Action Definitions ──

interface ActionDef {
  id: string
  label: string
  icon: React.ElementType
  eventType: OutreachEventType
  newStatus: OutreachStatus
  charLimit: number | null
  color: string
  needsText: boolean
  suggestContext: string
}

const ACTIONS: ActionDef[] = [
  {
    id: "invite",
    label: "Enviar Convite de Conexao",
    icon: UserPlus,
    eventType: "invite_sent",
    newStatus: "invite_sent",
    charLimit: 300,
    color: "#3b82f6",
    needsText: true,
    suggestContext: "invite",
  },
  {
    id: "message",
    label: "Enviar Mensagem",
    icon: MessageSquare,
    eventType: "message_sent",
    newStatus: "msg_sent",
    charLimit: 8000,
    color: "#8b5cf6",
    needsText: true,
    suggestContext: "message",
  },
  {
    id: "inmail",
    label: "Enviar InMail",
    icon: Mail,
    eventType: "inmail_sent",
    newStatus: "msg_sent",
    charLimit: 1900,
    color: "#0ea5e9",
    needsText: true,
    suggestContext: "inmail",
  },
  {
    id: "follow_up",
    label: "Follow-up",
    icon: RotateCw,
    eventType: "follow_up",
    newStatus: "follow_up",
    charLimit: 8000,
    color: "#06b6d4",
    needsText: true,
    suggestContext: "follow_up",
  },
  {
    id: "replied",
    label: "Marcar como Respondeu",
    icon: MessageCircle,
    eventType: "manual_note",
    newStatus: "replied",
    charLimit: null,
    color: "#f59e0b",
    needsText: false,
    suggestContext: "",
  },
  {
    id: "qualified",
    label: "Marcar como Qualificado",
    icon: CheckCircle,
    eventType: "manual_note",
    newStatus: "qualified",
    charLimit: null,
    color: "#10b981",
    needsText: false,
    suggestContext: "",
  },
  {
    id: "not_interested",
    label: "Marcar como Nao Interessado",
    icon: XCircle,
    eventType: "manual_note",
    newStatus: "not_interested",
    charLimit: null,
    color: "#6b7280",
    needsText: false,
    suggestContext: "",
  },
]

// ── Timeline Helpers ──

function getEventBadgeClass(type: string): string {
  const classes: Record<string, string> = {
    invite_sent: "bg-gray-500/20 text-gray-400",
    invite_accepted: "bg-green-500/20 text-green-400",
    message_sent: "bg-blue-500/20 text-blue-400",
    message_received: "bg-blue-500/20 text-blue-300",
    msg_sent: "bg-blue-500/20 text-blue-400",
    inmail_sent: "bg-orange-500/20 text-orange-400",
    inmail_received: "bg-orange-500/20 text-orange-300",
    follow_up: "bg-yellow-500/20 text-yellow-400",
    profile_viewed: "bg-purple-500/20 text-purple-400",
    note: "bg-gray-500/20 text-gray-400",
    manual_note: "bg-gray-500/20 text-gray-400",
    csv_import: "bg-teal-500/20 text-teal-400",
  }
  return classes[type] || "bg-gray-500/20 text-gray-400"
}

function getEventLabel(type: string): string {
  const labels: Record<string, string> = {
    invite_sent: "Convite",
    invite_accepted: "Aceito",
    message_sent: "Mensagem",
    message_received: "Recebida",
    msg_sent: "Mensagem",
    inmail_sent: "InMail",
    inmail_received: "InMail Rec.",
    follow_up: "Follow-up",
    profile_viewed: "Perfil",
    note: "Nota",
    manual_note: "Nota",
    csv_import: "CSV",
  }
  return labels[type] || type
}

function formatDate(iso: string): string {
  if (!iso) return ""
  const d = new Date(iso)
  return (
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) +
    " " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  )
}

// ── Other Helpers ──

type Channel = "linkedin" | "email" | "whatsapp"

function buildWaUrl(number: string, text: string): string {
  const clean = number.replace(/\D/g, "")
  return `https://wa.me/${clean}?text=${encodeURIComponent(text)}`
}

function CharCounter({ current, limit }: { current: number; limit: number }) {
  const pct = current / limit
  const colorClass =
    pct > 1 ? "text-red-500 font-bold" : pct > 0.95 ? "text-red-500" : pct > 0.8 ? "text-amber-500" : "text-muted-foreground"
  return (
    <span className={cn("text-xs tabular-nums", colorClass)}>
      {current} / {limit} caracteres
    </span>
  )
}

// ── Main Component ──

interface OutreachActionPanelProps {
  item: QueueItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  readOnly?: boolean
}

export default function OutreachActionPanel({ item, open, onOpenChange, readOnly = false }: OutreachActionPanelProps) {
  const [activeAction, setActiveAction] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [confirming, setConfirming] = useState(false)

  // 3-channel message state
  const [activeChannel, setActiveChannel] = useState<Channel>("linkedin")
  const [linkedinText, setLinkedinText] = useState("")
  const [emailSubject, setEmailSubject] = useState("")
  const [emailBody, setEmailBody] = useState("")
  const [whatsappText, setWhatsappText] = useState("")
  const [copied, setCopied] = useState(false)

  // Opportunity selector
  const [selectedOppId, setSelectedOppId] = useState<string>("")

  // Mark-connected local state
  const [isConnected, setIsConnected] = useState(false)
  const [showNextStep, setShowNextStep] = useState(false)
  const [connectedToast, setConnectedToast] = useState(false)

  // Timeline accordion
  const [timelineOpen, setTimelineOpen] = useState(false)

  const queryClient = useQueryClient()
  const createEvent = useCreateEvent()
  const updateStatus = useUpdateOutreachStatus(item?.lead_id ?? "")
  const generateMessages = useGenerateMessages()
  const markConnected = useMarkConnected()

  // New hooks
  const { data: nextStep, refetch: refetchNextStep } = useNextStepSuggestion(item?.lead_id || null)
  const { data: timeline } = useContactTimeline(item?.lead_id || null)

  // Fetch opportunities for this lead
  const { data: oppsData } = useOpportunities({ leadId: item?.lead_id || undefined })
  const opportunities = oppsData?.items || []

  // Derive connected status from item
  const effectiveConnected = isConnected || item?.outreach_status === "connected" || item?.outreach_status === "msg_sent" || item?.outreach_status === "replied" || item?.outreach_status === "follow_up" || item?.outreach_status === "qualified"

  // Contact whatsapp
  const contactWhatsapp = item?.contact_whatsapp

  // Reset state when panel opens/closes or item changes
  useEffect(() => {
    if (open) {
      setActiveAction(null)
      setActiveChannel("linkedin")
      setLinkedinText("")
      setEmailSubject("")
      setEmailBody("")
      setWhatsappText("")
      setCopied(false)
      setConfirmed(false)
      setConfirming(false)
      setSelectedOppId("")
      setIsConnected(false)
      setShowNextStep(false)
      setConnectedToast(false)
      setTimelineOpen(false)
    }
  }, [open, item?.lead_id])

  const handleActionClick = useCallback(
    async (action: ActionDef) => {
      if (!item) return

      if (!action.needsText) {
        // Direct status update (no text needed)
        setActiveAction(action.id)
        setConfirming(true)
        try {
          await createEvent.mutateAsync({
            lead_id: item.lead_id,
            contact_id: item.contact_id || undefined,
            event_type: action.eventType,
            channel: "linkedin",
            direction: "outbound",
            subject: `${action.label} - ${item.contact_name || item.company_name}`,
          })
          await updateStatus.mutateAsync(action.newStatus)
          setConfirmed(true)
          // Refresh all related queries
          queryClient.invalidateQueries({ queryKey: ["outreach-queue"] })
          queryClient.invalidateQueries({ queryKey: ["outreach-metrics"] })
          queryClient.invalidateQueries({ queryKey: ["conversion-funnel"] })
          queryClient.invalidateQueries({ queryKey: ["contact-timeline", item.lead_id] })
          queryClient.invalidateQueries({ queryKey: ["next-step", item.lead_id] })
          setTimeout(() => onOpenChange(false), 800)
        } catch (err) {
          console.error("Action failed:", err)
        } finally {
          setConfirming(false)
        }
        return
      }

      // Text action — generate AI messages for all 3 channels
      setActiveAction(action.id)
      setCopied(false)
      setConfirmed(false)
      setActiveChannel("linkedin")

      try {
        const result = await generateMessages.mutateAsync({
          leadId: item.lead_id,
          actionType: action.suggestContext,
          opportunityId: selectedOppId || undefined,
        })
        setLinkedinText(result.linkedin?.text || "")
        setEmailSubject(result.email?.subject || "")
        setEmailBody(result.email?.body || "")
        setWhatsappText(result.whatsapp?.text || "")
      } catch {
        // Fallback — set empty messages so user can type
        setLinkedinText("")
        setEmailSubject("")
        setEmailBody("")
        setWhatsappText("")
      }
    },
    [item, createEvent, updateStatus, generateMessages, onOpenChange, selectedOppId, queryClient],
  )

  // Handle action click from next-step suggestion button
  const handleSuggestedAction = useCallback(
    (actionId: string | null) => {
      if (!actionId) return
      const action = ACTIONS.find((a) => a.id === actionId)
      if (action) handleActionClick(action)
    },
    [handleActionClick],
  )

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!item || !activeAction) return
    const action = ACTIONS.find((a) => a.id === activeAction)
    if (!action) return

    // Determine which channel text to save
    let bodyText = linkedinText
    let channel = "linkedin"
    if (activeChannel === "email") {
      bodyText = `Assunto: ${emailSubject}\n\n${emailBody}`
      channel = "email"
    } else if (activeChannel === "whatsapp") {
      bodyText = whatsappText
      channel = "whatsapp"
    }

    setConfirming(true)
    try {
      await createEvent.mutateAsync({
        lead_id: item.lead_id,
        contact_id: item.contact_id || undefined,
        event_type: action.eventType,
        channel,
        direction: "outbound",
        subject: `${action.label} - ${item.contact_name || item.company_name}`,
        body: bodyText,
      })
      await updateStatus.mutateAsync(action.newStatus)
      setConfirmed(true)
      // Refresh everything
      queryClient.invalidateQueries({ queryKey: ["outreach-queue"] })
      queryClient.invalidateQueries({ queryKey: ["contact-timeline", item.lead_id] })
      queryClient.invalidateQueries({ queryKey: ["next-step", item.lead_id] })
      queryClient.invalidateQueries({ queryKey: ["outreach-metrics"] })
      queryClient.invalidateQueries({ queryKey: ["conversion-funnel"] })
      queryClient.invalidateQueries({ queryKey: ["outreach-events"] })
      setTimeout(() => onOpenChange(false), 800)
    } catch (err) {
      console.error("Confirm failed:", err)
    } finally {
      setConfirming(false)
    }
  }, [item, activeAction, activeChannel, linkedinText, emailSubject, emailBody, whatsappText, createEvent, updateStatus, onOpenChange, queryClient])

  const handleMarkConnected = useCallback(async () => {
    if (!item) return
    try {
      await markConnected.mutateAsync(item.lead_id)
      // Auto-refresh + local state update
      queryClient.invalidateQueries({ queryKey: ["outreach-queue"] })
      queryClient.invalidateQueries({ queryKey: ["next-step", item.lead_id] })
      queryClient.invalidateQueries({ queryKey: ["outreach-metrics"] })
      queryClient.invalidateQueries({ queryKey: ["conversion-funnel"] })
      queryClient.invalidateQueries({ queryKey: ["contact-timeline", item.lead_id] })
      setIsConnected(true)
      setShowNextStep(true)
      setConnectedToast(true)
      // Refetch next step
      setTimeout(() => refetchNextStep(), 500)
      // Auto-hide toast after 3s
      setTimeout(() => setConnectedToast(false), 3000)
    } catch (err) {
      console.error("Mark connected failed:", err)
    }
  }, [item, markConnected, queryClient, refetchNextStep])

  const openLinkedIn = useCallback(() => {
    if (!item?.contact_linkedin_url) return
    const url = item.contact_linkedin_url.startsWith("http")
      ? item.contact_linkedin_url
      : `https://${item.contact_linkedin_url}`
    window.open(url, "_blank", "noopener,noreferrer")
  }, [item])

  if (!item) return null

  const statusCfg = OUTREACH_STATUS_CONFIG[item.outreach_status as OutreachStatus]
  const isGenerating = generateMessages.isPending

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            "fixed right-0 top-0 z-50 h-full w-full max-w-lg border-l bg-background shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "duration-300 overflow-y-auto",
          )}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-background border-b px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold truncate">
                  {item.contact_name || "Sem contato"}
                </h2>
                {item.contact_job_title && (
                  <p className="text-sm text-muted-foreground truncate">{item.contact_job_title}</p>
                )}
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {item.lead_title || item.company_name}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {statusCfg && (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: statusCfg.color + "20", color: statusCfg.color }}
                    >
                      {statusCfg.title}
                    </span>
                  )}
                  {item.cadence_name && (
                    <span className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {item.cadence_name}
                    </span>
                  )}
                  {/* Mark as Connected button - hidden if already connected */}
                  {!readOnly && !effectiveConnected && item.outreach_status !== "not_interested" && (
                    <button
                      onClick={handleMarkConnected}
                      disabled={markConnected.isPending}
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-green-500/40 text-green-600 dark:text-green-400 hover:bg-green-500/10 transition-colors"
                    >
                      {markConnected.isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Link2 className="w-3 h-3" />
                      )}
                      Ja sou conectado
                    </button>
                  )}
                  {/* Connected indicator */}
                  {effectiveConnected && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-500/15 text-green-500">
                      <CheckCircle className="w-3 h-3" />
                      Conectado
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {item.contact_linkedin_url && (
                  <button
                    onClick={openLinkedIn}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:opacity-80"
                    style={{ backgroundColor: "#0A66C2" }}
                    title="Abrir perfil no LinkedIn"
                  >
                    <Linkedin className="w-4 h-4 text-white" />
                  </button>
                )}
                <Dialog.Close asChild>
                  <button className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-accent transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
          </div>

          {/* Connected Toast */}
          {connectedToast && (
            <div className="mx-5 mt-3 p-3 rounded-lg bg-green-500/15 border border-green-500/30 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Marcado como conectado!</span>
            </div>
          )}

          {/* Success State */}
          {confirmed && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-lg font-semibold text-green-600">Acao registrada!</p>
              <p className="text-sm text-muted-foreground mt-1">Status atualizado com sucesso</p>
            </div>
          )}

          {/* Read-only Warning */}
          {!confirmed && readOnly && (
            <div className="px-5 py-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border">
                <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Este lead pertence a outro responsavel.</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Somente visualizacao — acoes nao disponiveis.</p>
                </div>
              </div>
            </div>
          )}

          {/* No Contact Warning */}
          {!confirmed && !readOnly && !item?.has_contact && (
            <div className="px-5 pt-4 pb-2">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Lead sem contato principal</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Vincule um contato na tela de Leads para iniciar o outreach.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── A. Not Connected — First Approach CTA ── */}
          {!confirmed && !readOnly && !effectiveConnected && item?.has_contact && item.outreach_status === "not_contacted" && !activeAction && (
            <div className="px-5 pt-4 pb-2">
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30">
                <p className="text-sm font-medium text-foreground">Este contato ainda nao e sua conexao</p>
                <p className="text-xs text-muted-foreground mt-1">Gere uma mensagem de abordagem personalizada para enviar junto com o convite de conexao.</p>
                <button
                  onClick={() => handleSuggestedAction("invite")}
                  className="mt-3 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  Gerar Convite de Conexao
                </button>
              </div>
            </div>
          )}

          {/* ── B. Next Step Suggestion Banner ── */}
          {!confirmed && !readOnly && (showNextStep || effectiveConnected) && nextStep && !activeAction && (
            <div className="px-5 pt-3 pb-1">
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-blue-400 font-medium uppercase tracking-wider">Proximo passo sugerido</p>
                    <p className="text-sm text-foreground mt-1">{nextStep.suggestion.label}</p>
                    {nextStep.has_cadence && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Cadencia: {nextStep.cadence_name} — Step {nextStep.cadence_step + 1}/{nextStep.total_steps}
                      </p>
                    )}
                  </div>
                  {nextStep.suggestion.needs_text && nextStep.suggestion.action && (
                    <button
                      onClick={() => handleSuggestedAction(nextStep.suggestion.action)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors flex items-center gap-2 flex-shrink-0"
                    >
                      <Sparkles className="h-4 w-4" />
                      Gerar Mensagem
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Opportunity Selector (if lead has opportunities) */}
          {!confirmed && !readOnly && opportunities.length > 0 && (
            <div className="px-5 pt-3 pb-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Contexto da Oportunidade (opcional)
              </label>
              <select
                value={selectedOppId}
                onChange={(e) => setSelectedOppId(e.target.value)}
                className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">Nenhuma — usar contexto geral</option>
                {opportunities.map((opp) => (
                  <option key={opp.id} value={opp.id}>
                    {opp.name} — {opp.stage}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Action Buttons */}
          {!confirmed && !readOnly && (
            <div className="px-5 py-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Acoes de Outreach
              </p>
              {ACTIONS.map((action) => {
                const Icon = action.icon
                const isActive = activeAction === action.id
                return (
                  <div key={action.id}>
                    <button
                      onClick={() => handleActionClick(action)}
                      disabled={confirming || (!item?.has_contact && action.needsText)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-left",
                        isActive
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border hover:bg-accent/50 hover:border-accent",
                        confirming && "opacity-50 cursor-not-allowed",
                        !item?.has_contact && action.needsText && "opacity-40 cursor-not-allowed",
                      )}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: action.color + "15" }}
                      >
                        {confirming && isActive && !action.needsText ? (
                          <Loader2 className="w-4 h-4 animate-spin" style={{ color: action.color }} />
                        ) : (
                          <Icon className="w-4 h-4" style={{ color: action.color }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{action.label}</span>
                        {action.charLimit && (
                          <span className="text-[10px] text-muted-foreground ml-2">
                            (max {action.charLimit} chars)
                          </span>
                        )}
                      </div>
                      {isActive && action.needsText && (
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                    </button>

                    {/* ── 3-Channel Tabs (Expanded) ── */}
                    {isActive && action.needsText && (
                      <div className="mt-3 ml-2 mr-2 mb-2 space-y-0 animate-in slide-in-from-top-2 duration-200 border rounded-lg overflow-hidden">
                        {/* Loading state */}
                        {isGenerating && (
                          <div className="flex items-center gap-2 p-4 bg-blue-500/10">
                            <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                            <span className="text-blue-400 text-sm">Gerando mensagens com IA...</span>
                          </div>
                        )}

                        {/* Channel tabs */}
                        {!isGenerating && (
                          <>
                            <div className="flex border-b border-border bg-muted/30">
                              {(["linkedin", "email", "whatsapp"] as Channel[]).map((ch) => (
                                <button
                                  key={ch}
                                  onClick={() => setActiveChannel(ch)}
                                  className={cn(
                                    "flex-1 px-4 py-2.5 text-xs font-medium transition-colors",
                                    activeChannel === ch
                                      ? "text-primary border-b-2 border-primary bg-background"
                                      : "text-muted-foreground hover:text-foreground",
                                  )}
                                >
                                  {ch === "linkedin" && "LinkedIn"}
                                  {ch === "email" && "Email"}
                                  {ch === "whatsapp" && "WhatsApp"}
                                </button>
                              ))}
                            </div>

                            {/* LinkedIn Tab */}
                            {activeChannel === "linkedin" && (
                              <div className="p-3 space-y-2">
                                <textarea
                                  value={linkedinText}
                                  onChange={(e) => setLinkedinText(e.target.value)}
                                  rows={action.charLimit && action.charLimit <= 300 ? 4 : 8}
                                  className={cn(
                                    "w-full rounded-lg border bg-background px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
                                    action.charLimit && linkedinText.length > action.charLimit && "border-red-500 focus:ring-red-500/30",
                                  )}
                                  placeholder="Mensagem LinkedIn..."
                                />
                                <div className="flex justify-between items-center">
                                  {action.charLimit && (
                                    <CharCounter current={linkedinText.length} limit={action.charLimit} />
                                  )}
                                  <div className="flex gap-2 ml-auto">
                                    <button
                                      onClick={() => handleCopy(linkedinText)}
                                      className={cn(
                                        "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all",
                                        copied ? "bg-green-500/10 border-green-500/30 text-green-600" : "hover:bg-accent",
                                      )}
                                    >
                                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                      {copied ? "Copiado!" : "Copiar"}
                                    </button>
                                    {item.contact_linkedin_url && (
                                      <button
                                        onClick={openLinkedIn}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-accent transition-colors"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        Abrir LinkedIn
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Email Tab */}
                            {activeChannel === "email" && (
                              <div className="p-3 space-y-2">
                                <input
                                  value={emailSubject}
                                  onChange={(e) => setEmailSubject(e.target.value)}
                                  placeholder="Assunto do email..."
                                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                />
                                <textarea
                                  value={emailBody}
                                  onChange={(e) => setEmailBody(e.target.value)}
                                  rows={10}
                                  className="w-full rounded-lg border bg-background px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                  placeholder="Corpo do email..."
                                />
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleCopy(`Assunto: ${emailSubject}\n\n${emailBody}`)}
                                    className={cn(
                                      "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all",
                                      copied ? "bg-green-500/10 border-green-500/30 text-green-600" : "hover:bg-accent",
                                    )}
                                  >
                                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    {copied ? "Copiado!" : "Copiar Tudo"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* WhatsApp Tab */}
                            {activeChannel === "whatsapp" && (
                              <div className="p-3 space-y-2">
                                <textarea
                                  value={whatsappText}
                                  onChange={(e) => setWhatsappText(e.target.value)}
                                  rows={6}
                                  className={cn(
                                    "w-full rounded-lg border bg-background px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
                                    whatsappText.length > 1000 && "border-red-500 focus:ring-red-500/30",
                                  )}
                                  placeholder="Mensagem WhatsApp..."
                                />
                                <div className="flex justify-between items-center">
                                  <CharCounter current={whatsappText.length} limit={1000} />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleCopy(whatsappText)}
                                      className={cn(
                                        "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all",
                                        copied ? "bg-green-500/10 border-green-500/30 text-green-600" : "hover:bg-accent",
                                      )}
                                    >
                                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                      {copied ? "Copiado!" : "Copiar"}
                                    </button>
                                    {contactWhatsapp && (
                                      <a
                                        href={buildWaUrl(contactWhatsapp, whatsappText)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white transition-all"
                                        style={{ backgroundColor: "#25D366" }}
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        Enviar WhatsApp
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Confirm & Register */}
                            <div className="flex justify-end p-3 pt-2 border-t border-border">
                              <button
                                onClick={handleConfirm}
                                disabled={confirming}
                                className={cn(
                                  "inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg text-white transition-all",
                                  confirming ? "bg-primary/60 cursor-not-allowed" : "bg-primary hover:bg-primary/90",
                                )}
                              >
                                {confirming ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3.5 h-3.5" />
                                )}
                                Confirmar e Registrar
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* ── D. Contact Timeline Accordion ── */}
          {!confirmed && item?.lead_id && (
            <div className="px-5 pb-4">
              <div className="border-t border-border pt-4">
                <button
                  onClick={() => setTimelineOpen(!timelineOpen)}
                  className="flex items-center justify-between w-full text-left group"
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", timelineOpen && "rotate-90")} />
                    <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                      Historico de Interacoes
                    </span>
                    {timeline && timeline.total > 0 && (
                      <span className="px-1.5 py-0.5 bg-muted text-muted-foreground text-[10px] font-medium rounded">
                        {timeline.total}
                      </span>
                    )}
                  </div>
                </button>

                {timelineOpen && (
                  <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto animate-in slide-in-from-top-2 duration-200">
                    {!timeline || timeline.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic py-2">Nenhuma interacao registrada</p>
                    ) : (
                      timeline.items.map((ev) => (
                        <div key={ev.id} className="flex gap-3 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border border-transparent hover:border-border">
                          <div className="flex-shrink-0 mt-0.5">
                            {ev.direction === "outbound" ? (
                              <ArrowUpRight className="h-4 w-4 text-blue-400" />
                            ) : (
                              <ArrowDownLeft className="h-4 w-4 text-green-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium", getEventBadgeClass(ev.event_type))}>
                                {getEventLabel(ev.event_type)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {formatDate(ev.created_at)}
                              </span>
                              {ev.channel && ev.channel !== "manual" && (
                                <span className="text-[10px] text-muted-foreground/60">
                                  {ev.channel}
                                </span>
                              )}
                            </div>
                            {ev.subject && (
                              <p className="text-xs text-foreground/80 mt-1 truncate">{ev.subject}</p>
                            )}
                            {ev.body && (
                              <details className="mt-1.5">
                                <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                                  Ver mensagem completa
                                </summary>
                                <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap bg-background/80 p-2.5 rounded border border-border max-h-[150px] overflow-y-auto leading-relaxed">
                                  {ev.body}
                                </p>
                              </details>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <Dialog.Title className="sr-only">Acoes de Outreach</Dialog.Title>
          <Dialog.Description className="sr-only">
            Painel de acoes LinkedIn para {item.contact_name}
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
